import {
  Box,
  Button,
  capitalize,
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
} from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { DataGridPro } from '../../tables/DataGridPro';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import ServiceLevelDialog from './ServiceLevelDialog';
import { TransportationService } from '../../../data/models/transportation-service.model';

const PREFIX = 'ClientServiceLevels';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  statusSelect: `${PREFIX}-statusSelect`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  addButton: `${PREFIX}-addButton`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
  [`& .${classes.header}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.statusSelect}`]: {
    minWidth: '220px',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.addButton}`]: {
    width: 150,
  },
}));

export enum ServiceLevelState {
  Draft = 'draft',
  Active = 'active',
  Inactive = 'inactive',
  Deleted = 'deleted',
  Archived = 'archived',
}

export default function ClientServiceLevels() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
    ServiceLevelState.Draft,
    ServiceLevelState.Active,
  ]);
  const selectableStatuses = [
    ServiceLevelState.Draft,
    ServiceLevelState.Active,
    ServiceLevelState.Inactive,
  ];

  const client = useAppSelector(clientSlice.selectCurrentClient);
  // TODO: update this when service levels are implemented at the client level
  const serviceLevels = useAppSelector(
    accountingSlice.selectTransportationServices,
  );
  // const serviceLevels = useAppSelector(clientSlice.selectServiceLevels);
  const loading = useAppSelector(
    accountingSlice.selectLoadingTransportationServices,
  );
  // const loading = useAppSelector(clientSlice.selectLoadingServiceLevels);
  const transportationAttributeCategories = useAppSelector(
    lookupSlice.selectTransportationAttributeCategories,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const pricingModels = useAppSelector(
    accountingSlice.selectActivePricingModels,
  );
  const [searchText, setSearchText] = useState('');
  const [selectedServiceLevel, setSelectedServiceLevel] =
    useState<TransportationService>();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (client) {
      // TODO: add this back in when Client service levels are implemented on the back end
      // dispatch(clientSlice.fetchClientServiceLevelsAsync(client.clientBC));
      dispatch(accountingSlice.fetchTransportationServicesAsync());
      dispatch(accountingSlice.fetchPricingFunctionsAsync());
      dispatch(accountingSlice.fetchTransportationAttributesAsync());
      dispatch(accountingSlice.fetchPricingModelsAsync());
    }
  }, [client]);

  const filteredServiceLevels = useMemo(() => {
    if (searchText) {
      return serviceLevels.filter((sl) => {
        const lcName = sl.name.toLowerCase();
        const lcSearchText = searchText.toLowerCase();
        return lcName.indexOf(lcSearchText) >= 0;
      });
    }
    // TODO: once we have real data we need to filter on status
    return serviceLevels;
  }, [searchText, serviceLevels, selectedStatuses]);

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    const newStatuses = typeof value === 'string' ? value.split(',') : value;
    setSelectedStatuses(newStatuses);
  };

  const renderPricingModel = (params: GridRenderCellParams) => {
    return 'TDB';
  };

  const renderServicePlans = (params: GridRenderCellParams) => {
    return 'TDB';
  };

  const renderStatus = (params: GridRenderCellParams) => {
    return 'TDB';
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('client.serviceLevelName'),
      sortable: false,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'pricingModel',
      headerName: t('client.pricingModel'),
      sortable: false,
      minWidth: 200,
      renderCell: renderPricingModel,
    },
    {
      field: 'servicePlans',
      headerName: t('client.servicePlans'),
      sortable: false,
      minWidth: 200,
      renderCell: renderServicePlans,
    },
    {
      field: 'status',
      headerName: t('client.status'),
      sortable: false,
      minWidth: 200,
      renderCell: renderStatus,
    },
  ];

  const handleAddServiceLevel = () => {
    setSelectedServiceLevel(undefined);
    setDialogOpen(true);
  };

  const editServiceLevel = (params: GridRowParams<any>) => {
    setSelectedServiceLevel(params.row);
    setDialogOpen(true);
  };

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('client.serviceLevels')}
          returnLink={`/clients/${client?.clientBC}`}
          returnLinkText={client?.legalName}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <Stack direction="row" className={classes.header}>
            <Stack direction="row">
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<SearchIcon />}
                label={t('client.search')}
                currentSearchFilter={searchText}
                displayClearButton={true}
                onSearchChange={handleSearchTextChange}
              />
              <Select
                labelId="select-status-label"
                multiple
                value={selectedStatuses as any}
                onChange={handleStatusChange}
                className={classes.statusSelect}
                input={<OutlinedInput label="Tag" />}
                renderValue={(selected) =>
                  (selected as any)
                    .map((val: string) => capitalize(val))
                    .join(', ')
                }>
                {selectableStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedStatuses.includes(status)} />
                    <ListItemText primary={capitalize(status)} />
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Button
              className={classes.addButton}
              color="primary"
              variant="contained"
              onClick={handleAddServiceLevel}>
              {t('common.add')}
            </Button>
          </Stack>
          <DataGridPro
            disableColumnMenu={true}
            getRowId={(row) => row.transportationServiceUUID}
            rows={filteredServiceLevels}
            columns={columns}
            pagination={false}
            autoHeight
            loading={loading}
            hideFooter={true}
            rowSelection={true}
            onRowClick={editServiceLevel}
          />
        </Stack>
      </Box>
      {client && (
        <ServiceLevelDialog
          title={t('client.addServiceLevel')}
          clientBC={client.clientBC}
          clientServiceLevels={serviceLevels}
          transportationAttributeCategories={transportationAttributeCategories}
          transportationAttributes={transportationAttributes}
          pricingModels={pricingModels}
          serviceLevel={selectedServiceLevel}
          open={dialogOpen}
          close={() => setDialogOpen(false)}
        />
      )}
    </StyledPaper>
  );
}
